import React from 'react';
import {
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from 'common/schema';
import { common } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FormProvider from 'common/components/form-provider';
import background from 'assets/images/backgrounds/pre-register-bg.png';
import fbIcon from 'assets/images/icons/fb-icon-filled.png';
import ggIcon from 'assets/images/icons/gg-icon.svg';
import Logo from 'common/components/logo';
import { useTheme } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import useLogin from 'common/hooks/use-login';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { toast } from 'react-toastify';

const PreRegisterForm = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [language, setLanguage] = React.useState(() => {
        const savedLanguage = localStorage.getItem('i18nextLng') || 'vi';
        return savedLanguage;
    });
    const navigate = useNavigate();
    const methods = useForm({
        resolver: yupResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
            rememberMe: true,
        },
    });

    const { i18n } = useTranslation();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        i18n.changeLanguage(e.target.value);
        setLanguage(e.target.value);
        localStorage.setItem('i18nextLng', e.target.value);
    };
    const { loginWithGoogle, loginWithFacebook, onSuccessLoginFacebook } =
        useLogin();

    return (
        <Box sx={styles.layout}>
            <Box
                sx={{
                    flex: 2,
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'bottom',
                    backgroundRepeat: 'no-repeat',
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: common.white,
                }}
            >
                <Box padding={6}>
                    <FormProvider methods={methods}>
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <Logo width={90} height={70} />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Typography variant="h3" sx={styles.textTitle}>
                                    {t('register.title')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Stack spacing={2}>
                                    <Button
                                        onClick={() => loginWithGoogle()}
                                        variant="outlined"
                                        sx={{
                                            ...styles.button,
                                            fontSize: '14px',
                                            color: '#333333',
                                        }}
                                        color="primary"
                                        fullWidth
                                        startIcon={
                                            <img src={ggIcon} alt="Google" />
                                        }
                                    >
                                        {t('register.with.google')}
                                    </Button>
                                    <FacebookLogin
                                        appId={
                                            process.env
                                                .REACT_APP_FACEBOOK_APP_ID || ''
                                        }
                                        onSuccess={onSuccessLoginFacebook}
                                        onFail={() => {
                                            toast.error(
                                                'Kết nối đến Facebook thất bại, vui lòng thử lại.'
                                            );
                                        }}
                                        scope="public_profile,email"
                                        fields="id,name,email,picture"
                                        onProfileSuccess={(response) => {
                                            loginWithFacebook(response);
                                        }}
                                        render={(renderProps) => (
                                            <Button
                                                onClick={renderProps.onClick}
                                                variant="outlined"
                                                sx={{
                                                    ...styles.button,
                                                    fontSize: '14px',
                                                    color: '#333333',
                                                }}
                                                color="primary"
                                                fullWidth
                                                startIcon={
                                                    <img
                                                        src={fbIcon}
                                                        alt="Facebook"
                                                    />
                                                }
                                            >
                                                {t('register.with.facebook')}
                                            </Button>
                                        )}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                        <Divider
                            sx={{ mt: '32px', mb: '32px', color: '#8F959E' }}
                        >
                            {t('register.or')}
                        </Divider>
                        <Grid item xs={12} md={12}>
                            <Stack marginBottom={'29px'}>
                                <Button
                                    onClick={() => navigate('/register')}
                                    sx={{
                                        ...styles.button,
                                        backgroundColor: '#000',
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        '&:hover': {
                                            backgroundColor: '#000',
                                        },
                                    }}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('register.using.email.or.phone')}
                                </Button>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: '#333',
                                        mt: 3,
                                    }}
                                >
                                    {t('register.using.terms.and.conditions')}{' '}
                                    <Typography
                                        component={'a'}
                                        href="#"
                                        sx={{
                                            fontSize: '12px',
                                            color: 'var(--color-primary)',
                                            mt: 3,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        {t('register.terms.and.conditions')}
                                    </Typography>{' '}
                                    {t('register.and')}{' '}
                                    <Typography
                                        component={'a'}
                                        href="#"
                                        sx={{
                                            fontSize: '12px',
                                            color: 'var(--color-primary)',
                                            mt: 3,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        {t('register.privacy.policy')}
                                    </Typography>{' '}
                                    , {t('register.including')}{' '}
                                    <Typography
                                        component={'a'}
                                        href="#"
                                        sx={{
                                            fontSize: '12px',
                                            color: 'var(--color-primary)',
                                            mt: 3,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        {t('register.using.cookies')}
                                    </Typography>{' '}
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <Stack marginBottom={'29px'} spacing={1}>
                                <Typography fontSize={'14px'}>
                                    {t('register.already.have.account')}
                                </Typography>
                                <Button
                                    onClick={() => navigate('/login')}
                                    disableRipple
                                    sx={{
                                        ...styles.button,
                                        fontSize: '14px',
                                        color: '#333333',
                                    }}
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                >
                                    {t('common.login')}
                                </Button>
                            </Stack>
                        </Grid>
                        <Select
                            labelId="language"
                            id="language"
                            size="small"
                            value={language}
                            startAdornment={
                                <LanguageIcon
                                    sx={{
                                        color: theme.palette.text.primary,
                                        marginRight: '8px',
                                    }}
                                />
                            }
                            onChange={(event) =>
                                handleChange(
                                    event as React.ChangeEvent<HTMLInputElement>
                                )
                            }
                            sx={{ mt: 10 }}
                        >
                            <MenuItem value={'en'}>English</MenuItem>
                            <MenuItem value={'vi'}>Việt Nam</MenuItem>
                        </Select>
                    </FormProvider>
                </Box>
            </Box>
        </Box>
    );
};

const styles = createStyleSheet({
    layout: {
        width: '100vw',
        minHeight: '100vh',
        overflowY: 'auto',
        backgroundAttachment: 'fixed',
        display: 'flex',
    },
    textTitle: {
        fontSize: '20px',
        fontWeight: 'bold',
        lineHeight: '28px',
        marginTop: '48px',
        marginBottom: '24px',
    },
    textLink: {
        cursor: 'pointer',
        fontSize: '20px',
    },
    button: {
        lineHeight: '24.2px',
        textTransform: 'none',
        height: 42,
        borderRadius: '6px',
    },
    styleInput: {
        '& .MuiInputBase-root': {
            height: '48px',
            fontSize: '16px',
            backgroundColor: '#F2F2F2',
            borderRadius: '6px',
        },
    },
});

export default PreRegisterForm;
